import { z } from 'zod';
import { emailSchema } from './accounts.js';
import { participantKindSchema } from './participants.js';
import { activityStatuses } from './program.js';

/**
 * PRD 15 M6 Obsługa wydarzenia: kod QR rodziny (US-601), odprawa (US-602…US-606), obecność na zajęciach (US-607).
 * Odprawa = osoba przyszła na edycję; obecność = osoba przyszła na konkretne zajęcia. To dwie niezależne informacje.
 */

const editionRefSchema = z.object({ slug: z.string(), name: z.string(), city: z.string(), date: z.string() });
export type EditionRef = z.infer<typeof editionRefSchema>;

// --- Odprawa (US-602…US-605) ---

export const checkInPersonSchema = z.object({
  participantId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  isGuardian: z.boolean(),
  /** Właściciel konta bywa uczniem (PRD §10 pkt 61) – wtedy `child`. */
  kind: participantKindSchema,
  /** „8 lat” / „dorosły” – wiek w roku edycji (AC-2). */
  ageLabel: z.string(),
  /** Zgłoszona do edycji. Niezgłoszone osoby konta – do dopisania (US-603). */
  registered: z.boolean(),
  checkedInAt: z.string().nullable(),
  checkedInBy: z.string().nullable(),
  /** Dzisiejsze zajęcia osoby (godzina, symbol, sala). */
  activities: z.array(z.object({ time: z.string(), symbol: z.string(), title: z.string(), room: z.string() })),
});
export type CheckInPerson = z.infer<typeof checkInPersonSchema>;

/** Okno rodziny (US-602 AC-2). */
export const checkInFamilySchema = z.object({
  edition: editionRefSchema,
  account: z.object({ id: z.string().uuid(), firstName: z.string(), lastName: z.string(), email: z.string() }),
  registered: z.boolean(),
  /** Prowadzący – rola konta albo zajęcia w tej edycji. Punkt rejestracji musi to wyraźnie widzieć. */
  speaker: z.boolean().default(false),
  /** Nieodwołane zajęcia, które konto prowadzi w tej edycji. */
  teaching: z
    .array(z.object({ time: z.string(), symbol: z.string(), title: z.string(), room: z.string() }))
    .default([]),
  /** AC-6: od dnia poprzedzającego do końca dnia wydarzenia, edycja opublikowana. */
  checkInOpen: z.boolean(),
  people: z.array(checkInPersonSchema),
});
export type CheckInFamily = z.infer<typeof checkInFamilySchema>;

/**
 * AC-3, US-604: zmiany z okna rodziny – kogo odprawić, komu cofnąć odprawę. Zmiany, nie pełny stan: dwa stanowiska
 * odprawiające tę samą rodzinę naraz nie nadpiszą sobie zaznaczeń (AC-8).
 */
export const checkInSaveSchema = z.object({
  checkIn: z.array(z.string().uuid()).max(50),
  undo: z.array(z.string().uuid()).max(50),
});
export type CheckInSave = z.infer<typeof checkInSaveSchema>;

/** AC-1, AC-5: kod QR – rodzina tej albo innej edycji. Kod nieznany → błąd `qr_invalid`. */
export const qrLookupSchema = z.object({
  accountId: z.string().uuid(),
  edition: editionRefSchema,
});
export type QrLookup = z.infer<typeof qrLookupSchema>;

/** US-605: wynik wyszukiwania – e‑mail częściowo zamaskowany (AC-2). */
export const checkInSearchResultSchema = z.object({
  results: z.array(
    z.object({
      accountId: z.string().uuid(),
      guardian: z.string(),
      emailMasked: z.string(),
      registered: z.boolean(),
      /** Osoby zgłoszone do edycji (dla niezgłoszonego konta – wszystkie osoby konta). */
      people: z.array(z.string()),
      checkedIn: z.number().int(),
      /** Prowadzący – rola konta albo nieodwołane zajęcia w tej edycji. */
      speaker: z.boolean().default(false),
    }),
  ),
});
export type CheckInSearchResult = z.infer<typeof checkInSearchResultSchema>;

// --- Rejestracja na miejscu (US-606) ---

const personName = z.string().trim().min(1, { message: 'To pole jest wymagane.' }).max(80, { message: 'Maksymalnie 80 znaków.' });

export const onSiteRegistrationSchema = z.object({
  email: emailSchema,
  firstName: personName,
  lastName: personName,
  children: z
    .array(
      z.object({
        firstName: personName,
        lastName: personName,
        birthYear: z.coerce.number().int().min(1900).max(2100),
      }),
    )
    .max(10),
  acceptedConsentVersionIds: z.array(z.string().uuid()),
});
export type OnSiteRegistration = z.infer<typeof onSiteRegistrationSchema>;
export const onSiteRegistrationResultSchema = z.object({ accountId: z.string().uuid() });

// --- Obecność na zajęciach (US-607) ---

export const attendanceActivitySchema = z.object({
  id: z.string().uuid(),
  symbol: z.string(),
  title: z.string(),
  room: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: z.enum(activityStatuses),
  /** Zajęcia z zapisami (bez zapisów nie ma listy obecności). */
  enrollment: z.boolean(),
  enrolled: z.number().int(),
  attended: z.number().int(),
  capacity: z.number().int(),
  reserve: z.number().int(),
  /** Prowadzący tych zajęć to zalogowana osoba. */
  mine: z.boolean(),
  /** Lista obecności potwierdzona (pierwsze zaznaczenie albo „Potwierdź listę”) – US-802 AC-2. */
  confirmed: z.boolean(),
});
export type AttendanceActivity = z.infer<typeof attendanceActivitySchema>;

export const attendanceActivityListSchema = z.object({
  edition: editionRefSchema,
  activities: z.array(attendanceActivitySchema),
});
export type AttendanceActivityList = z.infer<typeof attendanceActivityListSchema>;

/** AC-6 (BR-44): tylko imię, nazwisko i wiek – bez e‑maili i danych kont. */
export const attendancePersonSchema = z.object({
  participantId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  ageLabel: z.string(),
  checkedIn: z.boolean(),
  attendedAt: z.string().nullable(),
});
export type AttendancePerson = z.infer<typeof attendancePersonSchema>;

export const attendanceDetailSchema = z.object({
  edition: editionRefSchema,
  activity: attendanceActivitySchema,
  /** AC-4: od N minut przed startem do końca dnia wydarzenia. */
  open: z.boolean(),
  opensAt: z.string(),
  people: z.array(attendancePersonSchema),
});
export type AttendanceDetail = z.infer<typeof attendanceDetailSchema>;

export const setAttendanceSchema = z.object({ attended: z.boolean() });
export type SetAttendance = z.infer<typeof setAttendanceSchema>;

/** AC-5: osoby zgłoszone do edycji do dopisania na zajęcia. */
export const attendanceCandidatesSchema = z.object({
  candidates: z.array(
    z.object({
      participantId: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
      ageLabel: z.string(),
      enrolled: z.boolean(),
    }),
  ),
});
export type AttendanceCandidates = z.infer<typeof attendanceCandidatesSchema>;

export const addAttendeeSchema = z.object({ participantId: z.string().uuid() });
export type AddAttendee = z.infer<typeof addAttendeeSchema>;
